import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  FileText,
  Workflow as WorkflowIcon,
  ShieldCheck,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Sparkles,
  ArrowRight,
  Upload,
  Stethoscope,
  Target,
  Gauge,
  TrendingUp,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/stores/auth';
import { cn, formatDate } from '@/lib/utils';
import { ROLE_RANK } from '@praxara/shared/constants';
import { ActivityFeed } from '@/components/layout/ActivityFeed';
import { FirstRunWizard } from '@/components/layout/FirstRunWizard';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);

  // Admins must complete the onboarding wizard before they can use the dashboard.
  // Non-admins see the dashboard immediately -- they were invited into a tenant that
  // is still being set up, but they don't drive the wizard themselves.
  if (user?.role === 'ADMIN' && tenant && !tenant.onboardingCompletedAt) {
    return <Navigate to="/onboarding" replace />;
  }
  const [wizardDismissed, setWizardDismissed] = useState(() =>
    (typeof localStorage !== 'undefined' && localStorage.getItem('praxara.wizard-dismissed')) === '1',
  );

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: async () => (await api.get('/api/projects')).data,
  });

  const docs = useQuery({
    queryKey: ['documents', 'recent'],
    queryFn: async () => (await api.get('/api/documents?pageSize=5')).data,
  });

  const runs = useQuery({
    queryKey: ['runs', 'recent'],
    queryFn: async () => (await api.get('/api/workflows/runs/list?pageSize=5')).data,
  });

  // Cheap probe used only to decide whether to surface the first-run wizard.
  // We skip the wizard on any tenant that already has PV cases — typical for
  // demo / advisor accounts where seeded content makes the setup screen
  // misleading. Failure falls back to total=0 so onboarding still works on
  // brand-new tenants if /api/pv/cases ever changes shape.
  const pvCases = useQuery({
    queryKey: ['pv-cases', 'wizard-probe'],
    queryFn: async () => {
      try {
        return (await api.get('/api/pv/cases?pageSize=1')).data as { total?: number };
      } catch {
        return { total: 0 };
      }
    },
  });

  const usage = useQuery({
    queryKey: ['billing', 'usage', 'dashboard'],
    queryFn: async () => (await api.get('/api/billing/usage')).data,
    enabled: !!user && ROLE_RANK[user.role] >= ROLE_RANK['MANAGER'],
  });

  const awaitingReview = runs.data?.items?.filter(
    (r: { status: string }) => r.status === 'AWAITING_REVIEW',
  ).length ?? 0;
  const runningNow = runs.data?.items?.filter(
    (r: { status: string }) => r.status === 'RUNNING' || r.status === 'QUEUED',
  ).length ?? 0;

  // Show the first-run wizard ONLY when the tenant looks empty across every
  // surface that the wizard's templates would seed. For demo / advisor
  // tenants (e.g. biontech-demo with seeded projects, documents, signals,
  // and PV cases) any of these counts being non-zero auto-skips the modal
  // so reviewers land on their queue instead of a setup screen.
  const tenantHasContent =
    (projects.data?.total ?? 0) > 0 ||
    (docs.data?.total ?? 0) > 0 ||
    (runs.data?.total ?? 0) > 0 ||
    (pvCases.data?.total ?? 0) > 0;
  const wizardDataReady =
    !projects.isLoading && !docs.isLoading && !runs.isLoading && !pvCases.isLoading;
  const showWizard = !wizardDismissed && wizardDataReady && !tenantHasContent;

  return (
    <div className="space-y-6">
      {showWizard && (
        <FirstRunWizard
          onDismiss={() => {
            localStorage.setItem('praxara.wizard-dismissed', '1');
            setWizardDismissed(true);
          }}
        />
      )}
      {/* Hero */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">
            Welcome back, <span className="text-primary">{user?.firstName}</span>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {tenant?.name} · {tenant?.plan} plan ·{' '}
            {tenant?.gxpValidated ? (
              <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <ShieldCheck className="h-3 w-3" /> GxP validated
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" /> Pre-production · not for clinical use
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild>
            <Link to="/documents">
              <Upload className="mr-1.5 h-4 w-4" /> Upload document
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/workflows">
              <WorkflowIcon className="mr-1.5 h-4 w-4" /> Run workflow
            </Link>
          </Button>
        </div>
      </div>

      {/* Getting started guide — shows for new tenants with < 3 documents */}
      {(docs.data?.total ?? 0) < 3 && !docs.isLoading && (
        <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300">
                <ArrowRight className="h-4 w-4" />
              </div>
              <div>
                <div className="font-medium text-blue-900 dark:text-blue-200">How to get started</div>
                <ol className="mt-2 space-y-1.5 text-sm text-blue-800 dark:text-blue-300">
                  <li><strong>1. Upload a document</strong> — Go to <Link to="/documents" className="underline">Documents</Link>, drag a PDF (CSR, EPAR, PSMF, RMP, or any regulatory doc).</li>
                  <li><strong>2. Wait for extraction</strong> — Text is extracted automatically (~30 seconds). Status changes to "Extracted".</li>
                  <li><strong>3. Run a workflow</strong> — Click the document, then pick a workflow (e.g. "CSR Extraction" or "PSMF Compliance Scan"). The AI processes it.</li>
                  <li><strong>4. Review + approve</strong> — Open the result, review the AI output, approve with your 21 CFR Part 11 e-signature (password + reason).</li>
                  <li><strong>5. Check the audit trail</strong> — Go to <Link to="/audit" className="underline">Audit trail</Link> → "Verify integrity" to see the SHA-256 chain.</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action needed callout */}
      {awaitingReview > 0 && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-primary">
              <Clock className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="font-medium">
                {awaitingReview} extraction{awaitingReview > 1 ? 's' : ''} awaiting your review
              </div>
              <div className="text-xs text-muted-foreground">
                Approve or correct AI-generated outputs to move workflows to completed.
              </div>
            </div>
            <Button asChild size="sm">
              <Link to="/workflows">
                Review now <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Snapshot CTA — hidden on demo / evaluation tenants */}
      {!(tenant?.name?.includes('Demo') || tenant?.plan === 'PROFESSIONAL') && (
        <Card className="border-emerald-500/30 bg-gradient-to-r from-emerald-950/40 to-primary/5">
          <CardContent className="flex flex-wrap items-center gap-6 p-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-emerald-500/20 text-emerald-400">
              <Sparkles className="h-7 w-7" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-3">
                <h3 className="font-display text-lg font-semibold">AI Readiness Snapshot</h3>
                <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 font-mono text-xs">£2,500</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                1-page audit-ready report mapping your current AI use to EU AI Act + Annex 22 + 21 CFR Part 11.
                Procurement-friendly — no security review needed. Delivered in 1 week.
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                <a href="https://praxara.io/snapshot" target="_blank" rel="noopener noreferrer">
                  Get started <ArrowRight className="ml-1.5 h-3.5" />
                </a>
              </Button>
              <Button asChild variant="outline" size="sm">
                <a href="https://praxara.io/trust" target="_blank" rel="noopener noreferrer">
                  <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Trust Center
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stat cards */}
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard
          icon={FileText}
          label="Documents"
          value={docs.data?.total ?? 0}
          to="/documents"
          trend={docs.isLoading ? undefined : 'Click to manage'}
        />
        <StatCard
          icon={WorkflowIcon}
          label="Active runs"
          value={runningNow}
          to="/workflows"
          tone="info"
          trend={`${runs.data?.total ?? 0} total runs`}
        />
        <StatCard
          icon={Clock}
          label="Awaiting review"
          value={awaitingReview}
          to="/workflows"
          tone={awaitingReview > 0 ? 'warning' : 'muted'}
        />
        {usage.data ? (
          <StatCard
            icon={Gauge}
            label="Pages used"
            value={(usage.data.pagesUsed ?? 0).toLocaleString()}
            to="/settings/usage"
            tone={(usage.data.utilisation ?? 0) > 0.8 ? 'warning' : 'muted'}
            trend={`${Math.round((usage.data.utilisation ?? 0) * 100)}% of plan`}
          />
        ) : (
          <StatCard icon={ShieldCheck} label="Audit integrity" value="OK" to="/audit" tone="success" />
        )}
      </div>

      {/* Two-column: work + activity */}
      <div className="grid gap-4 lg:grid-cols-[1.4fr,1fr]">
        <div className="space-y-4">
          {/* Recent documents */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-base">Recent documents</CardTitle>
                <CardDescription>Latest uploads and extractions.</CardDescription>
              </div>
              <Button asChild variant="ghost" size="sm">
                <Link to="/documents">View all</Link>
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {docs.isLoading ? (
                <SkeletonList />
              ) : !docs.data?.items?.length ? (
                <div className="px-6 py-8 text-center text-sm text-muted-foreground">
                  No documents yet.{' '}
                  <Link to="/documents" className="text-primary hover:underline">
                    Upload your first
                  </Link>
                  .
                </div>
              ) : (
                <div className="divide-y">
                  {docs.data.items.map(
                    (doc: {
                      id: string;
                      fileName: string;
                      status: string;
                      createdAt: string;
                      pageCount: number | null;
                    }) => (
                      <Link
                        key={doc.id}
                        to={`/documents/${doc.id}`}
                        className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-accent/40"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{doc.fileName}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {doc.pageCount ?? '—'} pages · {formatDate(doc.createdAt)}
                          </div>
                        </div>
                        <StatusBadge status={doc.status} />
                      </Link>
                    ),
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent runs */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-base">Workflow runs</CardTitle>
                <CardDescription>Execution status across skills.</CardDescription>
              </div>
              <Button asChild variant="ghost" size="sm">
                <Link to="/workflows">View all</Link>
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {runs.isLoading ? (
                <SkeletonList />
              ) : !runs.data?.items?.length ? (
                <div className="px-6 py-8 text-center text-sm text-muted-foreground">
                  No runs yet. Upload a document and kick off a workflow.
                </div>
              ) : (
                <div className="divide-y">
                  {runs.data.items.map(
                    (run: {
                      id: string;
                      workflow: { name: string };
                      status: string;
                      createdAt: string;
                    }) => (
                      <Link
                        key={run.id}
                        to={`/workflows/runs/${run.id}`}
                        className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-accent/40"
                      >
                        <WorkflowIcon className="h-4 w-4 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{run.workflow.name}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {formatDate(run.createdAt)}
                          </div>
                        </div>
                        <RunStatusBadge status={run.status} />
                      </Link>
                    ),
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick links */}
          <div className="grid gap-3 md:grid-cols-3">
            <QuickLink
              to="/pv"
              icon={Stethoscope}
              title="Pharmacovigilance"
              description="ICSR intake, MedDRA coding, narratives."
              tone="bg-rose-500/10 text-rose-600"
            />
            <QuickLink
              to="/compliance"
              icon={Target}
              title="Compliance scanner"
              description="PSMF, SOP, GVP gap analysis."
              tone="bg-indigo-500/10 text-indigo-600"
              minRole="MANAGER"
            />
            <QuickLink
              to="/audit"
              icon={ShieldCheck}
              title="Audit trail"
              description="Tamper-evident, SHA-256 chained."
              tone="bg-emerald-500/10 text-emerald-600"
              minRole="REVIEWER"
            />
          </div>
        </div>

        {/* Activity feed */}
        <ActivityFeed limit={12} />
      </div>
    </div>
  );
}

// --- helpers -------------------------------------------

function StatCard({
  icon: Icon,
  label,
  value,
  to,
  tone = 'primary',
  trend,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
  to: string;
  tone?: 'primary' | 'info' | 'success' | 'warning' | 'muted';
  trend?: string;
}) {
  const toneCls: Record<string, string> = {
    primary: 'bg-primary/10 text-primary',
    info: 'bg-blue-500/10 text-blue-600',
    success: 'bg-emerald-500/10 text-emerald-600',
    warning: 'bg-amber-500/10 text-amber-600',
    muted: 'bg-muted text-muted-foreground',
  };
  return (
    <Link to={to}>
      <Card className="transition-all hover:shadow-md hover:border-primary/30">
        <CardContent className="flex items-start justify-between p-4">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {label}
            </div>
            <div className="mt-1 font-display text-2xl">{value}</div>
            {trend && (
              <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                <TrendingUp className="h-3 w-3" />
                {trend}
              </div>
            )}
          </div>
          <div className={cn('flex h-9 w-9 items-center justify-center rounded-md', toneCls[tone])}>
            <Icon className="h-4 w-4" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function QuickLink({
  to,
  icon: Icon,
  title,
  description,
  tone,
  minRole,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  tone: string;
  minRole?: 'MANAGER' | 'REVIEWER' | 'ADMIN' | 'OPERATOR' | 'VIEWER';
}) {
  const user = useAuthStore((s) => s.user);
  if (minRole && (!user || ROLE_RANK[user.role] < ROLE_RANK[minRole])) return null;
  return (
    <Link
      to={to}
      className="group block rounded-lg border bg-card p-4 transition-all hover:border-primary/40 hover:shadow-md"
    >
      <div className={cn('flex h-9 w-9 items-center justify-center rounded-md', tone)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-3 font-medium group-hover:text-primary">{title}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>
    </Link>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-2 p-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const v: Record<string, 'secondary' | 'info' | 'success' | 'destructive' | 'warning'> = {
    UPLOADED: 'secondary',
    PROCESSING: 'info',
    TEXT_EXTRACTED: 'success',
    PROCESSED: 'success',
    FAILED: 'destructive',
  };
  const label: Record<string, string> = {
    UPLOADED: 'Queued',
    PROCESSING: 'Processing',
    TEXT_EXTRACTED: 'Extracted',
    PROCESSED: 'Processed',
    FAILED: 'Failed',
  };
  return <Badge variant={v[status] ?? 'secondary'}>{label[status] ?? status}</Badge>;
}

function RunStatusBadge({ status }: { status: string }) {
  if (status === 'COMPLETED')
    return (
      <Badge variant="success">
        <CheckCircle2 className="mr-1 h-3 w-3" /> Completed
      </Badge>
    );
  if (status === 'FAILED' || status === 'CANCELLED')
    return (
      <Badge variant="destructive">
        <XCircle className="mr-1 h-3 w-3" /> {status}
      </Badge>
    );
  if (status === 'AWAITING_REVIEW') return <Badge variant="warning">Awaiting review</Badge>;
  if (status === 'RUNNING')
    return (
      <Badge variant="info">
        <Sparkles className="mr-1 h-3 w-3" /> Running
      </Badge>
    );
  return <Badge variant="info">{status}</Badge>;
}
